import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ExternalLink, Facebook, Instagram, Twitter, Linkedin, Youtube } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useLang();
  const [social, setSocial] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings/social-links`).then((r) => setSocial(r.data)).catch(() => {});
  }, []);

  const socialIcons = [
    { key: "facebook", Icon: Facebook, color: "hover:text-blue-400" },
    { key: "instagram", Icon: Instagram, color: "hover:text-pink-400" },
    { key: "twitter", Icon: Twitter, color: "hover:text-sky-400" },
    { key: "linkedin", Icon: Linkedin, color: "hover:text-blue-300" },
    { key: "youtube", Icon: Youtube, color: "hover:text-red-400" },
  ].filter((s) => social[s.key]);

  return (
    <footer className="bg-[#0B1121] text-slate-400 border-t border-white/[0.06]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 bg-orange-500 flex items-center justify-center font-black text-white text-sm"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                VS
              </div>
              <div>
                <div
                  className="text-white text-base leading-none"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em" }}
                >
                  VS Agro Infra Enterprises
                </div>
                <div className="text-orange-400 leading-none mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.15em" }}>VSINDLPARK.COM</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-slate-500">
              {t("footer.desc")}
            </p>
            <div className="mt-4 flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <span className="text-slate-500">IIE Sigaddi, SIIDCUL, Kotdwar,<br />Pauri Garhwal, Uttarakhand - 246149</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-5 uppercase tracking-wider text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.15em" }}>{t("footer.quickLinks")}</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: t("footer.availablePlots"), path: "/plots" },
                { label: t("footer.bookPlot"), path: "/booking" },
                { label: t("footer.photoGallery"), path: "/gallery" },
                { label: t("footer.schemesAndPolicies"), path: "/schemes" },
                { label: t("footer.financeOptions"), path: "/finance" },
                { label: "Blog", path: "/blog" },
                { label: t("footer.contactUs"), path: "/contact" },
              ].map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-slate-500 hover:text-orange-400 transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Plot Sizes */}
          <div>
            <h4 className="text-white font-semibold mb-5 uppercase tracking-wider text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.15em" }}>{t("footer.availableSizes")}</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["500 Sq.Mtr", "7,500"],
                ["1,000 Sq.Mtr", "7,500"],
                ["2,000 Sq.Mtr", "7,500"],
                ["5,000 Sq.Mtr", "7,500"],
                ["10,000 Sq.Mtr", "7,500"],
              ].map(([size, price]) => (
                <li key={size} className="flex justify-between text-slate-500">
                  <span>{size}</span>
                  <span className="text-orange-400" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.7rem" }}>
                    {`\u20B9${price}/sqmtr`}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-5 uppercase tracking-wider text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.15em" }}>{t("footer.getInTouch")}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <a href="tel:+919258959058" className="text-slate-500 hover:text-orange-400 transition-colors">+91-9258959058</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <a href="tel:+919557877290" className="text-slate-500 hover:text-orange-400 transition-colors">+91-9557877290</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <a href="mailto:amritinfotech9@gmail.com" className="text-slate-500 hover:text-orange-400 transition-colors text-xs">
                  amritinfotech9@gmail.com
                </a>
              </li>
            </ul>
            <div className="mt-5 space-y-1.5">
              <a href="https://siidcul.com" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-orange-400 transition-colors">
                <ExternalLink className="w-3 h-3" /> SIIDCUL Official Site
              </a>
              <br />
              <a href="https://sidauk.in" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-orange-400 transition-colors">
                <ExternalLink className="w-3 h-3" /> SIDA Official Site
              </a>
            </div>
            {socialIcons.length > 0 && (
              <div className="mt-5 flex items-center gap-3" data-testid="footer-social-links">
                {socialIcons.map(({ key, Icon, color }) => (
                  <a key={key} href={social[key]} target="_blank" rel="noopener noreferrer"
                    className={`text-slate-500 ${color} transition-colors`} data-testid={`footer-social-${key}`}>
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/[0.06] pt-8">
          {/* Large brand watermark */}
          <div className="text-center mb-6">
            <span className="text-white/[0.03] select-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: "clamp(3rem, 8vw, 7rem)", textTransform: "uppercase", lineHeight: 1, letterSpacing: "-0.03em" }}>
              VS AGRO
            </span>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-600">
            <span>{t("footer.rights")}</span>
            <Link to="/admin" className="hover:text-slate-400 transition-colors" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.1em" }}>{t("footer.adminPanel")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
